export type FeishuSyncPhase =
  | "reading"
  | "parsing"
  | "diffing"
  | "upserting"
  | "extracting"
  | "done";

export type FeishuSyncJobStatus = "running" | "completed" | "partial" | "error";

export type FeishuSyncTrigger = "manual" | "scheduled";

export interface FeishuSyncFailure {
  wxid: string;
  nickname: string;
  error: string;
}

export interface FeishuSyncResultSummary {
  group: string;
  newCount: number;
  changedCount: number;
  dataChangedCount: number;
  notesChangedCount: number;
  profilesExtracted: number;
  status: "success" | "partial" | "error";
  errorMessage?: string;
}

export interface FeishuAtmosphereSyncProgress {
  group: string;
  status: FeishuSyncJobStatus;
  phase: FeishuSyncPhase;
  trigger: FeishuSyncTrigger;
  startedAt: string;
  updatedAt: string;
  sheetTitle?: string;
  rowCount?: number;
  newCount?: number;
  changedCount?: number;
  notesChangedCount?: number;
  upsertDone?: number;
  upsertTotal?: number;
  extractDone?: number;
  extractTotal?: number;
  currentLabel?: string;
  failures?: FeishuSyncFailure[];
  result?: FeishuSyncResultSummary;
  errorMessage?: string;
}

export function computeFeishuSyncPercent(progress: FeishuAtmosphereSyncProgress): number {
  if (progress.status !== "running") {
    return 100;
  }

  const phaseBase: Record<FeishuSyncPhase, number> = {
    reading: 5,
    parsing: 15,
    diffing: 25,
    upserting: 60,
    extracting: 95,
    done: 100,
  };

  const base = phaseBase[progress.phase] ?? 0;

  if (progress.phase === "upserting" && progress.upsertTotal && progress.upsertTotal > 0) {
    const ratio = (progress.upsertDone ?? 0) / progress.upsertTotal;
    return Math.min(60, Math.round(25 + ratio * 35));
  }

  if (progress.phase === "extracting" && progress.extractTotal && progress.extractTotal > 0) {
    const ratio = (progress.extractDone ?? 0) / progress.extractTotal;
    return Math.min(99, Math.round(60 + ratio * 35));
  }

  return base;
}

export function formatFeishuSyncPhaseLabel(progress: FeishuAtmosphereSyncProgress): string {
  switch (progress.phase) {
    case "reading":
      return "读取飞书表格";
    case "parsing":
      return progress.rowCount != null
        ? `解析数据（${progress.rowCount} 行）`
        : "解析数据";
    case "diffing":
      return progress.changedCount != null
        ? `比对变更（新增 ${progress.newCount ?? 0}，变更 ${progress.changedCount}）`
        : "比对变更";
    case "upserting":
      return `写入成员（${progress.upsertDone ?? 0}/${progress.upsertTotal ?? 0}）`;
    case "extracting":
      return `AI 提取画像（${progress.extractDone ?? 0}/${progress.extractTotal ?? 0}）`;
    case "done":
      return "检查完成";
    default:
      return "处理中";
  }
}
